import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import { PencilIcon, ChartBarIcon, DocumentPlusIcon, ShoppingCartIcon, TrashIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { useForm } from 'react-hook-form'; // Import useForm for contact form

registerLocale('ru', ru);

// Interfaces
interface Contact {
  id: number;
  name: string;
  phone: string;
  brand?: string; // Added brand field
  clientId?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface Discount {
    id: number;
    discountPercentage: number;
    description?: string;
    effectiveFrom?: string;
    effectiveTo?: string | null;
    reason?: string;
    isActive?: boolean;
}

interface Order {
    id: number;
    orderNumber: string;
    totalAmount: number;
    finalAmount?: number;
    status: string;
    startDate?: string;
    endDate?: string;
    orderDate: string;
}

interface Quote {
    id: number;
    quoteNumber: string;
    quoteDate: string;
    finalAmount: number;
    status: string;
}

interface ClientDetailsData {
  id: number;
  name: string;
  director: string; 
  phone?: string | null; // 
  address?: string | null;
  email?: string | null;
  referralSource?: string | null;
  notes?: string | null;
  status: string;
  website?: string | null;
  industry?: string | null;
  taxId?: string | null;
  createdAt: string;
  updatedAt: string;
  Orders?: any[];
  Quotes?: any[];
  Contracts?: any[];
  Payments?: any[];
  Discounts?: any[];
  // Contacts are fetched separately now
}

interface MappedClientData {
    id: number;
    name: string;
    director: string; 
    phone: string; // Removed
    address: string;
    email: string;
    referralSource: string;
    notes: string;
    status: string;
    website: string;
    industry: string;
    taxId: string;
    createdAt: string;
    updatedAt: string;
    Orders?: Order[];
    Quotes?: Quote[];
    Discounts?: Discount[];
    // Contacts are managed in separate state
}

interface ClientStats {
  totalSpent: number;
  orderCount: number;
  quoteCount: number;
  averageOrderValue: number;
  firstOrderDate: string | null;
  latestOrderDate: string | null;
}

interface NewContactFormData {
    name: string;
    phone: string;
    brand?: string; // Added brand field
}

// Helper functions for status (keep existing ones)
const getOrderStatusText = (status: string): string => {
    const map: { [key: string]: string } = { 'new': 'Новый', 'processing': 'В обработке', 'completed': 'Завершен', 'cancelled': 'Отменен', 'reserved': 'Зарезервирован', 'waitingPayment': 'Ожидает оплаты', 'partiallyPaid': 'Частично оплачен', 'fullyPaid': 'Оплачен' };
    return map[status] || status;
};
const getOrderStatusClass = (status: string): string => {
    const map: { [key: string]: string } = { 'new': 'bg-blue-100 text-blue-800', 'processing': 'bg-yellow-100 text-yellow-800', 'completed': 'bg-green-100 text-green-800', 'cancelled': 'bg-red-100 text-red-800', 'reserved': 'bg-purple-100 text-purple-800', 'waitingPayment': 'bg-orange-100 text-orange-800', 'partiallyPaid': 'bg-yellow-100 text-yellow-800', 'fullyPaid': 'bg-teal-100 text-teal-800' };
    return map[status] || 'bg-gray-100 text-gray-800';
};
const getQuoteStatusText = (status: string): string => {
    const map: { [key: string]: string } = { 'draft': 'Черновик', 'sent': 'Отправлено', 'accepted': 'Принято', 'rejected': 'Отклонено', 'converted': 'Конвертировано в заказ' };
    return map[status] || status;
};
const getQuoteStatusClass = (status: string): string => {
    const map: { [key: string]: string } = { 'draft': 'bg-gray-100 text-gray-800', 'sent': 'bg-blue-100 text-blue-800', 'accepted': 'bg-green-100 text-green-800', 'rejected': 'bg-red-100 text-red-800', 'converted': 'bg-purple-100 text-purple-800' };
    return map[status] || 'bg-gray-100 text-gray-800';
};

// Main Component
const ClientDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [client, setClient] = useState<MappedClientData | null>(null);
    const [contacts, setContacts] = useState<Contact[]>([]); // State for contacts
    const [stats, setStats] = useState<ClientStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    const [contactSubmitError, setContactSubmitError] = useState<string | null>(null);

    const { register: registerContact, handleSubmit: handleContactSubmit, reset: resetContactForm, formState: { errors: contactErrors } } = useForm<NewContactFormData>();

    // Removed canManageContacts, will use specific permissions directly

    // Fetch Client Details (without contacts initially)
    const fetchClientDetails = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<ClientDetailsData>(`/clients/${id}`);
            const rawClientData = response.data;

            const mappedData: MappedClientData = {
                id: rawClientData.id,
                name: rawClientData.name,
                director: rawClientData.director || '-', // Use director
                phone: rawClientData.phone || '-',
                address: rawClientData.address || '-',
                email: rawClientData.email || '-',
                referralSource: rawClientData.referralSource || '-',
                notes: rawClientData.notes || '-',
                status: rawClientData.status,
                website: rawClientData.website || '-',
                industry: rawClientData.industry || '-',
                taxId: rawClientData.taxId || '-',
                createdAt: rawClientData.createdAt,
                updatedAt: rawClientData.updatedAt,
                Orders: rawClientData.Orders || [],
                Quotes: rawClientData.Quotes || [],
                Discounts: rawClientData.Discounts || [],
            };
            setClient(mappedData);

            // Calculate stats (assuming Orders are fetched with client details)
            setStats({
                totalSpent: rawClientData.Orders?.reduce((sum, order) => sum + (order.finalAmount || 0), 0) || 0,
                orderCount: rawClientData.Orders?.length || 0,
                quoteCount: rawClientData.Quotes?.length || 0,
                averageOrderValue: (rawClientData.Orders?.length || 0) > 0 ? (rawClientData.Orders?.reduce((sum, order) => sum + (order.finalAmount || 0), 0) || 0) / (rawClientData.Orders?.length || 1) : 0,
                firstOrderDate: rawClientData.Orders && rawClientData.Orders.length > 0 ? rawClientData.Orders.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0].createdAt : null,
                latestOrderDate: rawClientData.Orders && rawClientData.Orders.length > 0 ? rawClientData.Orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0].createdAt : null,
            });

        } catch (err) {
            console.error("Error fetching client details:", err);
            setError("Ошибка загрузки данных клиента");
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Fetch Contacts
    const fetchContacts = useCallback(async () => {
        if (!id) return;
        try {
            const response = await axios.get<Contact[]>(`/clients/${id}/contacts`);
            setContacts(response.data);
        } catch (err) {
            console.error("Error fetching contacts:", err);
            // Optionally set an error state specific to contacts
        }
    }, [id]);

    useEffect(() => {
        fetchClientDetails();
        fetchContacts();
    }, [fetchClientDetails, fetchContacts]);

    // --- Contact Handlers ---
    const onAddContactSubmit = async (data: NewContactFormData) => {
        if (!id || !can('contacts.create')) return; // Changed from canManageContacts
        setContactSubmitError(null);
        try {
            // Include brand in the payload
            const payload = { ...data, clientId: parseInt(id, 10) };
            await axios.post('/contacts', payload);
            setShowAddContactForm(false);
            resetContactForm();
            fetchContacts(); // Refresh contacts list
        } catch (err: any) {
            console.error("Error adding contact:", err);
            setContactSubmitError(err.response?.data?.message || "Ошибка при добавлении контакта");
        }
    };

    const handleDeleteContact = async (contactId: number) => {
        if (!can('contacts.delete') || !window.confirm('Вы уверены, что хотите удалить этот контакт?')) { // Changed from canManageContacts
            return;
        }
        try {
            await axios.delete(`/contacts/${contactId}`);
            fetchContacts(); // Refresh contacts list
        } catch (err) {
            console.error("Error deleting contact:", err);
            alert("Ошибка при удалении контакта");
        }
    };

    // --- Formatting Helpers ---
    const formatCurrency = (amount: number | null | undefined): string => {
        if (amount === null || amount === undefined || isNaN(amount)) return '-';
        return new Intl.NumberFormat('ru-KZ', { style: 'currency', currency: 'KZT', maximumFractionDigits: 2 }).format(amount);
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
        } catch (e) {
            return 'Invalid Date';
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error || !client) return <div className="text-center p-4 text-red-600">{error || "Клиент не найден"}</div>;

    return (
        <div>
            <PageHeader
                title={client.name}
                subtitle={`Клиент #${client.id}`}
                backLink="/clients"
                actions={
                    can('clients.edit') ? (
                        <Link to={`/clients/edit/${client.id}`} className="btn-primary flex items-center">
                            <PencilIcon className="h-5 w-5 mr-1" /> Редактировать
                        </Link>
                    ) : null
                }
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Client Info */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">Информация о клиенте</h2>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-gray-500">ID:</dt><dd className="text-gray-900">{client.id}</dd></div>
                            {client.director && <div className="flex justify-between"><dt className="text-gray-500">Директор:</dt><dd className="text-gray-900">{client.director}</dd></div>}
                            {client.phone && <div className="flex justify-between"><dt className="text-gray-500">Телефон:</dt><dd className="text-gray-900">{client.phone}</dd></div>}
                            {client.email && <div className="flex justify-between"><dt className="text-gray-500">Email:</dt><dd className="text-gray-900">{client.email}</dd></div>}
                            {client.address && <div className="flex justify-between"><dt className="text-gray-500">Адрес:</dt><dd className="text-gray-900">{client.address}</dd></div>}
                            {client.taxId && <div className="flex justify-between"><dt className="text-gray-500">БИН/ИИН:</dt><dd className="text-gray-900">{client.taxId}</dd></div>}
                            {client.industry && <div className="flex justify-between"><dt className="text-gray-500">Отрасль:</dt><dd className="text-gray-900">{client.industry}</dd></div>}
                        </dl>
                    </div>

                    {/* Contacts Section */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">Контакты</h2>
                            {can('contacts.create') && ( // Changed from canManageContacts
                                <button onClick={() => setShowAddContactForm(!showAddContactForm)} className="btn-outline btn-xs text-xs flex items-center">
                                    <UserPlusIcon className="h-3 w-3 mr-1" /> {showAddContactForm ? 'Отмена' : 'Добавить'}
                                </button>
                            )}
                        </div>

                        {/* Add Contact Form */}
                        {showAddContactForm && (
                            <form onSubmit={handleContactSubmit(onAddContactSubmit)} className="mb-4 p-4 border rounded bg-gray-50 space-y-3">
                                <h3 className="text-md font-medium">Новый контакт</h3>
                                {contactSubmitError && <p className="text-red-600 text-sm">{contactSubmitError}</p>}
                                <div>
                                    <label htmlFor="contactName" className="form-label text-sm">Имя *</label>
                                    <input
                                        id="contactName"
                                        type="text"
                                        className={`form-input form-input-sm ${contactErrors.name ? 'border-red-500' : ''}`}
                                        {...registerContact('name', { required: 'Имя обязательно' })}
                                    />
                                    {contactErrors.name && <p className="text-red-600 text-xs mt-1">{contactErrors.name.message}</p>}
                                </div>
                                <div>
                                    <label htmlFor="contactPhone" className="form-label text-sm">Телефон *</label>
                                    <input
                                        id="contactPhone"
                                        type="text"
                                        className={`form-input form-input-sm ${contactErrors.phone ? 'border-red-500' : ''}`}
                                        placeholder="+7..."
                                        {...registerContact('phone', {
                                            required: 'Телефон обязателен',
                                            pattern: { value: /^[+]?[0-9\s()-]{10,17}$/, message: 'Неверный формат' }
                                        })}
                                    />
                                    {contactErrors.phone && <p className="text-red-600 text-xs mt-1">{contactErrors.phone.message}</p>}
                                </div>
                                <div>
                                    <label htmlFor="contactBrand" className="form-label text-sm">Бренд (необязательно)</label>
                                    <input
                                        id="contactBrand"
                                        type="text"
                                        className="form-input form-input-sm"
                                        {...registerContact('brand')}
                                    />
                                </div>
                                <button type="submit" className="btn-primary btn-sm w-full">Сохранить контакт</button>
                            </form>
                        )}

                        {/* Contacts List */}
                        {contacts.length > 0 ? (
                            <ul className="space-y-2">
                                {contacts.map(contact => (
                                    <li key={contact.id} className="flex justify-between items-center text-sm border-b pb-1">
                                        <div>
                                            <span className="font-medium text-gray-900">{contact.name}</span>
                                            {contact.brand && <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded ml-1">{contact.brand}</span>}
                                            <span className="text-gray-500 ml-2">{contact.phone}</span>
                                        </div>
                                        {can('contacts.delete') && ( // Changed from canManageContacts
                                            <button onClick={() => handleDeleteContact(contact.id)} className="text-red-500 hover:text-red-700 p-1" title="Удалить контакт">
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 italic">Контакты не добавлены.</p>
                        )}
                    </div>

                    {/* Discount Info */}
                    <div className="bg-white rounded-lg shadow p-6">
                         <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold text-gray-800">Текущая скидка</h2>
                        </div>
                        {client.Discounts && client.Discounts.length > 0 && client.Discounts[0].isActive ? (
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Размер:</dt>
                                    <dd className="text-green-600 font-semibold">{client.Discounts[0].discountPercentage}%</dd>
                                </div>
                                {client.Discounts[0].description && (
                                    <div>
                                        <dt className="text-gray-500 mb-1">Описание:</dt>
                                        <dd className="text-gray-700 text-xs italic">{client.Discounts[0].description}</dd>
                                    </div>
                                )}
                                {client.Discounts[0].effectiveFrom && (
                                     <div className="flex justify-between">
                                         <dt className="text-gray-500">Начало:</dt>
                                         <dd className="text-gray-900">{formatDate(client.Discounts[0].effectiveFrom)}</dd>
                                     </div>
                                )}
                                {client.Discounts[0].effectiveTo !== undefined && (
                                     <div className="flex justify-between">
                                         <dt className="text-gray-500">Окончание:</dt>
                                         <dd className="text-gray-900">{client.Discounts[0].effectiveTo ? formatDate(client.Discounts[0].effectiveTo) : 'Действует'}</dd>
                                     </div>
                                 )}
                            </div>
                        ) : (
                           <p className="text-sm text-gray-500 italic">Активная скидка отсутствует.</p>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="bg-white rounded-lg shadow p-6">
                         <h2 className="text-lg font-semibold mb-4 text-gray-800">Статистика</h2>
                         {stats && (
                            <dl className="space-y-2 text-sm">
                                <div className="flex justify-between"><dt className="text-gray-500">Всего потрачено:</dt><dd className="text-gray-900 font-semibold">{formatCurrency(stats.totalSpent)}</dd></div>
                                <div className="flex justify-between"><dt className="text-gray-500">Всего заказов:</dt><dd className="text-gray-900">{stats.orderCount}</dd></div>
                                {stats.quoteCount > 0 && <div className="flex justify-between"><dt className="text-gray-500">Всего КП:</dt><dd className="text-gray-900">{stats.quoteCount}</dd></div>}
                                <div className="flex justify-between"><dt className="text-gray-500">Средний чек:</dt><dd className="text-gray-900">{formatCurrency(stats.averageOrderValue)}</dd></div>
                                {stats.firstOrderDate && <div className="flex justify-between"><dt className="text-gray-500">Первый заказ:</dt><dd className="text-gray-900">{formatDate(stats.firstOrderDate)}</dd></div>}
                                {stats.latestOrderDate && <div className="flex justify-between"><dt className="text-gray-500">Последний заказ:</dt><dd className="text-gray-900">{formatDate(stats.latestOrderDate)}</dd></div>}
                            </dl>
                         )}
                    </div>

                    {/* Actions */}
                    <div className="bg-white rounded-lg shadow p-6">
                         <h2 className="text-lg font-semibold mb-4 text-gray-800">Действия</h2>
                         <div className="space-y-2">
                             {can('quotes.create') && ( // Changed from isAllowed
                                 <Link to={`/quotes/new?clientId=${client.id}`} className="btn-outline w-full flex items-center justify-center">
                                     <DocumentPlusIcon className="h-5 w-5 mr-2"/> Создать КП
                                 </Link>
                             )}
                             {can('orders.create') && ( // Changed from isAllowed
                                 <Link to={`/orders/new?clientId=${client.id}`} className="btn-outline w-full flex items-center justify-center">
                                     <ShoppingCartIcon className="h-5 w-5 mr-2"/> Создать Заказ
                                 </Link>
                             )}
                             <Link to={`/reports/client/${client.id}`} className="btn-outline w-full flex items-center justify-center">
                                <ChartBarIcon className="h-5 w-5 mr-2"/> Смотреть отчеты
                             </Link>
                         </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Recent Orders */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b"><h2 className="text-lg font-semibold text-gray-800">Недавние заказы</h2></div>
                         {client.Orders && client.Orders.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead className="bg-gray-50"><tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№ Заказа</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Создан</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
                                    </tr></thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {client.Orders.map((order: Order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm font-medium">
                                                    <Link to={`/orders/${order.id}`} className="text-primary hover:text-primary-dark">{order.orderNumber}</Link>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{formatDate(order.orderDate)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${getOrderStatusClass(order.status)}`}>
                                                        {getOrderStatusText(order.status)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">{formatCurrency(order.finalAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                         ) : <p className="p-6 text-sm text-gray-500 italic">Заказы отсутствуют.</p>}
                         <div className="p-4 text-right border-t"><Link to={`/orders?clientId=${client.id}`} className="text-sm btn-link">Все заказы</Link></div>
                    </div>

                    {/* Recent Quotes */}
                     <div className="bg-white rounded-lg shadow">
                         <div className="p-6 border-b"><h2 className="text-lg font-semibold text-gray-800">Недавние КП</h2></div>
                         {client.Quotes && client.Quotes.length > 0 ? (
                             <div className="overflow-x-auto">
                                 <table className="min-w-full">
                                     <thead className="bg-gray-50"><tr>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№ КП</th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата</th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
                                     </tr></thead>
                                     <tbody className="bg-white divide-y divide-gray-200">
                                         {client.Quotes.map((quote: Quote) => (
                                             <tr key={quote.id} className="hover:bg-gray-50">
                                                 <td className="px-6 py-4 text-sm font-medium">
                                                      <Link to={`/quotes/${quote.id}`} className="text-primary hover:text-primary-dark">{quote.quoteNumber}</Link>
                                                 </td>
                                                 <td className="px-6 py-4 text-sm text-gray-500">{formatDate(quote.quoteDate)}</td>
                                                 <td className="px-6 py-4 whitespace-nowrap">
                                                     <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${getQuoteStatusClass(quote.status)}`}>
                                                         {getQuoteStatusText(quote.status)}
                                                     </span>
                                                 </td>
                                                 <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">{formatCurrency(quote.finalAmount)}</td>
                                             </tr>
                                         ))}
                                     </tbody>
                                 </table>
                             </div>
                         ) : <p className="p-6 text-sm text-gray-500 italic">КП отсутствуют.</p>}
                          <div className="p-4 text-right border-t"><Link to={`/quotes?clientId=${client.id}`} className="text-sm btn-link">Все КП</Link></div>
                     </div>

                    {/* Discount History */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b"><h2 className="text-lg font-semibold text-gray-800">История скидок</h2></div>
                         {client.Discounts && client.Discounts.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead className="bg-gray-50"><tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Скидка (%)</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Начало</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Окончание</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Причина/Описание</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                                    </tr></thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {client.Discounts.map((discount: Discount) => (
                                            <tr key={discount.id} className={`${!discount.isActive ? 'bg-gray-50' : ''}`}>
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{discount.discountPercentage}%</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{formatDate(discount.effectiveFrom)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{discount.effectiveTo ? formatDate(discount.effectiveTo) : '-'}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{discount.reason || discount.description || '-'}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${discount.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {discount.isActive ? 'Активна' : 'Архив'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                         ) : <p className="p-6 text-sm text-gray-500 italic">История скидок отсутствует.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientDetails;
